import { Injectable, inject } from '@angular/core';
import { Firestore, doc, collection, runTransaction, docData, getDocs, increment } from '@angular/fire/firestore';
import { Observable, of, from } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

export type ViewTargetType = 'model' | 'image' | 'video';

export interface ViewRecord {
  id: string;
  userId: string;
  targetType: ViewTargetType;
  targetId: string;
  lastViewedAt: Date;
}

const RE_VIEW_COOLDOWN_MS = 12 * 60 * 60 * 1000; // 12 hours

@Injectable({ providedIn: 'root' })
export class ViewsService {
  private firestore = inject(Firestore);
  private auth = inject(AuthService);

  private getUid(): string | null { return this.auth.getCurrentUid(); }

  private viewDocId(type: ViewTargetType, targetId: string): string { return `${type}_${targetId}`; }

  private targetCollection(type: ViewTargetType): string {
    switch (type) {
      case 'model': return 'models';
      case 'image': return 'images';
      case 'video': return 'videos';
      default: return 'models';
    }
  }

  hasViewedRecently(type: ViewTargetType, targetId: string): Observable<boolean> {
    return of(this.getUid()).pipe(
      switchMap(uid => {
        if (!uid) return of(false);
        const viewRef = doc(this.firestore, `users/${uid}/views/${this.viewDocId(type, targetId)}`);
        return docData(viewRef).pipe(
          map((data: any) => {
            if (!data || !data.lastViewedAt) return false;
            const lastViewed = (data.lastViewedAt instanceof Date) ? data.lastViewedAt : (data.lastViewedAt.toDate ? data.lastViewedAt.toDate() : new Date(data.lastViewedAt));
            return Date.now() - lastViewed.getTime() < RE_VIEW_COOLDOWN_MS;
          }),
          catchError(() => of(false))
        );
      })
    );
  }

  async recordView(type: ViewTargetType, targetId: string): Promise<void> {
    const uid = this.getUid();
    if (!uid) throw new Error('Not authenticated');

    const viewId = this.viewDocId(type, targetId);
    const viewRef = doc(this.firestore, `users/${uid}/views/${viewId}`);
    const targetRef = doc(this.firestore, `${this.targetCollection(type)}/${targetId}`);

    await runTransaction(this.firestore, async (transaction) => {
      const viewSnap = await transaction.get(viewRef);
      const targetSnap = await transaction.get(targetRef);
      if (!targetSnap.exists()) throw new Error('Target does not exist');

      const now = new Date();
      const canIncrement = (() => {
        if (!viewSnap.exists()) return true;
        const data: any = viewSnap.data();
        const lastViewed = data.lastViewedAt?.toDate ? data.lastViewedAt.toDate() : (data.lastViewedAt ? new Date(data.lastViewedAt) : null);
        if (!lastViewed) return true;
        return (now.getTime() - lastViewed.getTime()) >= RE_VIEW_COOLDOWN_MS;
      })();

      if (!viewSnap.exists()) {
        const record: ViewRecord = {
          id: viewId,
          userId: uid,
          targetType: type,
          targetId: targetId,
          lastViewedAt: now
        };
        transaction.set(viewRef, record);
        transaction.update(targetRef, { viewsCount: increment(1) });
      } else if (canIncrement) {
        transaction.update(viewRef, { lastViewedAt: now });
        transaction.update(targetRef, { viewsCount: increment(1) });
      } else {
        // Within cooldown; do nothing
      }
    });
  }

  getMyViews$(): Observable<ViewRecord[]> {
    return of(this.getUid()).pipe(
      switchMap(uid => {
        if (!uid) return of([] as ViewRecord[]);
        const viewsCol = collection(this.firestore, `users/${uid}/views`);
        return from(getDocs(viewsCol)).pipe(
          map((snapshot: any) => snapshot.docs.map((d: any) => ({ ...(d.data()), id: d.id })) as ViewRecord[])
        );
      })
    );
  }
}
